import classNames from "classnames";
import { FC, useEffect, useRef, useState } from "react";
import SvgChecked from "../icons/Checked";
import SvgUnchecked from "../icons/Unchecked";
import Icon from "./Icon";
import styles from "./Switch.module.css";

interface ReactSwitchProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

const Switch: FC<ReactSwitchProps> = (props: ReactSwitchProps) => {
  const { checked, disabled, onChange, id, ...rest } = props;

  const height = 28;
  const width = 56;

  const handleDiameter = height - 2;
  const checkedPos = Math.max(width - height, width - (height + handleDiameter) / 2);
  const uncheckedPos = Math.max(0, (height - handleDiameter) / 2);

  const [pos, setPos] = useState(checked ? checkedPos : uncheckedPos);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPos(checked ? checkedPos : uncheckedPos);
  }, [checked, checkedPos, uncheckedPos]);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    !disabled && onChange(event.currentTarget.checked);
  };

  const onClick = (event: React.MouseEvent) => {
    event.preventDefault();
    inputRef.current?.focus();
    !disabled && onChange(!checked);
  };

  return (
    <div>
      <div
        className={classNames(styles.switchContainer, {
          [styles.switchDisabled]: disabled,
          [styles.switchActive]: checked,
        })}
        style={{
          borderRadius: height / 2,
        }}
        onClick={onClick}
      >
        <div
          className={styles.wrapper}
          style={{
            height,
            width,
            margin: Math.max(0, (handleDiameter - height) / 2),
            borderRadius: height / 2,
          }}
        >
          <div
            className={classNames(styles.icon, styles.iconChecked, { [styles.iconActive]: checked })}
            style={{
              height,
              width: Math.min(height * 1.5, width - (handleDiameter + height) / 2 + 1),
            }}
          >
            <Icon glyph={SvgChecked} />
          </div>
          <div
            className={classNames(styles.icon, styles.iconUnchecked, { [styles.iconActive]: !checked })}
            style={{
              height,
              width: Math.min(height * 1.5, width - (handleDiameter + height) / 2 + 1),
            }}
          >
            <Icon glyph={SvgUnchecked} />
          </div>
        </div>
        <div
          className={styles.handle}
          style={{
            height: handleDiameter,
            width: handleDiameter,
            transform: `translateX(${pos}px)`,
            top: Math.max(0, (height - handleDiameter) / 2),
          }}
        ></div>
        <input
          ref={inputRef}
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          className={styles.checkbox}
          onChange={onInputChange}
          {...rest}
        />
      </div>
    </div>
  );
};

export default Switch;
